import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  type TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';

import { uiReducer } from './slices/ui.slice';
import { authReducer } from './slices/auth.slice';
import { itemsReducer } from './slices/items.slice';
import { baseApi } from './api/base.api';
import './api/auth.api';
import './api/items.api';
import './api/suppliers.api';
import './api/purchaseOrders.api';
import "./api/inventory.api";
import "./api/ingredients.api";
import "./api/preparedItems.api";
import "./api/producedOutput.api";
import "./api/branchRequests.api";
import "./api/transfers.api";
import { suppliersReducer } from './slices/suppliers.slice';
import { purchaseOrdersReducer } from './slices/purchaseOrders.slice';
import { inventoryReducer } from "./slices/inventory.slice";
import { transfersReducer } from './slices/transfers.slice';
import { preparedItemsReducer } from './slices/preparedItems.slice';

export const store = configureStore({
  reducer: {
    ui: uiReducer,
    auth: authReducer,
    items: itemsReducer,
    suppliers: suppliersReducer,
    purchaseOrders: purchaseOrdersReducer,
    inventory: inventoryReducer,
    transfers: transfersReducer,
    preparedItems: preparedItemsReducer,
    [baseApi.reducerPath]: baseApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }).concat(baseApi.middleware),
  devTools: import.meta.env.DEV,
});

// Setup listeners for RTK Query
setupListeners(store.dispatch);

// Infer types from store
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
